import { useEffect, useMemo, useState } from 'react';

import { ActiveComponent } from '@/app/form/PhraseForm';
import { useEnv } from '@/components/providers/env-provider';
import { PlannerEnv } from '@/utils/env';
import { ConfiguratorApi } from '@/utils/ConfiguratorApi';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { PreviewSwitchControl } from '@/components/common/phrases/previewMode';
import { BannerLargePreview } from '../banner-large';
import { BannerMiniLinkPreview } from '../banner-mini-link';
import { PlannerContentPagePreview } from '../content-page/content-page';
import {
  PlannerConfigEntityWithOptionalFields,
  PlannerConfigMarketDataEntity,
} from '@/services/configurator/PlannerConfigTypes';
import { SwitchConfig } from '@/components/common/phrases/phrases';
import { toast } from 'sonner';
import SelectWithSearch from '@/components/common/SelectWithSearch/SelectWithSearch';
import { CircleAlert } from 'lucide-react';
import { useTheme } from '@/components/providers/theme-provider';

function FragmentPreview() {
  const [translations, setTranslations] = useState<Record<string, string>>({});
  const [plannerIds, setPlannerIds] = useState<string[]>([]);
  const [planner, setPlanner] = useState<PlannerConfigEntityWithOptionalFields>(
    {} as PlannerConfigEntityWithOptionalFields,
  );
  const [plannerIdValue, setPlannerIdValue] = useState('');
  const [marketLocale, setMarketLocale] = useState('');
  const [toggleJson, setToggleJson] = useState(false);
  const [activeComponent, setActiveComponent] =
    useState<ActiveComponent>('largeBanner');
  const { env } = useEnv();
  const { theme } = useTheme();

  const onToggle = (component: ActiveComponent) =>
    setActiveComponent(component);

  useEffect(() => {
    if (plannerIdValue) {
      setTranslations({});
      setMarketLocale('');
      ConfiguratorApi.getInstance()
        .getPublishedPlanner(env as PlannerEnv, plannerIdValue)
        .then((it) => {
          setPlanner(it);
        })
        .catch(() => {
          toast.error(`Failed to fetch ${plannerIdValue}`);
        });
    }
  }, [env, plannerIdValue]);

  useMemo(() => {
    ConfiguratorApi.getInstance()
      .getPlannerIdentifiers(env as PlannerEnv)
      .then((data) => setPlannerIds(data))
      .catch(() => toast.error('Failed to fetch planner ids'));
  }, [env]);

  const contentPageEnabled = planner.showInPages?.includes('content-page');

  useEffect(() => {
    if (marketLocale && planner.identifier) {
      const splitValue = marketLocale.split('-');
      const countryCode = splitValue[0];
      const languageCode = splitValue[1];
      ConfiguratorApi.getInstance()
        .getTranslation(planner.identifier, env, countryCode, languageCode)
        .then((data: Record<string, string>) => setTranslations(data))
        .catch(() => toast.error('Failed to fetch planner ids'));
    }
  }, [marketLocale, planner]);

  const switchConfigs: SwitchConfig[] = useMemo(() => {
    const showInPages = planner.showInPages || [];
    return [
      {
        id: 'banner-mini-visibility',
        label: 'Banner Mini',
        component: 'miniBanner',
        isVisible: showInPages.includes('pip'),
      },
      {
        id: 'banner-large-visibility',
        label: 'Banner Large',
        component: 'largeBanner',
        isVisible: showInPages.includes('pip') || showInPages.includes('plp'),
      },
      {
        id: 'content-page-visibility',
        label: 'Content Page',
        component: 'contentPage',
        isVisible: !!contentPageEnabled,
      },
    ];
  }, [contentPageEnabled, planner.showInPages]);

  const getMarketOverrideValues = (): PlannerConfigMarketDataEntity => {
    return (
      planner?.marketData?.find((plan) => plan.locale === marketLocale) ||
      ({} as PlannerConfigMarketDataEntity)
    );
  };
  return (
    <div className='flex w-full gap-8'>
      <div className='flex flex-col sticky h-fit border-1 p-4 rounded'>
        <h1 className='mb-2 font-bold text-2xl'>Select market and language</h1>
        <p className='mb-4'>
          Preview your published fragments based on market & locale
        </p>
        <SelectWithSearch
          list={plannerIds.map((plannerId) => ({
            value: plannerId,
            label: plannerId,
          }))}
          value={plannerIdValue}
          setValue={setPlannerIdValue}
          id='planner-identifier'
          fallbackSearchText='Select planner'
          label='Planner identifier'
          emptySearchFallbackText='No identifier found'
        />
        <div className='mt-4 flex flex-col'>
          {plannerIdValue && (
            <>
              {planner &&
              planner.marketData &&
              planner?.marketData?.length > 0 ? (
                <SelectWithSearch
                  list={planner.marketData
                    ?.filter((market) => market.isActive)
                    .map((item) => ({
                      value: item.locale,
                      label: item.locale,
                    }))}
                  value={marketLocale}
                  setValue={setMarketLocale}
                  id='market-dropdown'
                  fallbackSearchText='Select market/language'
                  label='Country/Market'
                  emptySearchFallbackText='No market/locale found'
                />
              ) : (
                <p className='flex items-center justify-between text-red-500'>
                  No markets activated
                  <CircleAlert size={24} color='#fb2c36' />
                </p>
              )}
            </>
          )}
          {marketLocale && (
            <div className='flex mt-4 flex-col border-1 p-2 rounded'>
              <p className='mb-2'>Toggle json output for translations</p>
              <div className='flex items-center'>
                <Switch
                  id='json-toggle'
                  checked={toggleJson}
                  defaultChecked={false}
                  onCheckedChange={(checked) => setToggleJson(checked)}
                  className='mr-2 my-2'
                />
                <Label htmlFor='json-toggle'>Json</Label>
              </div>
            </div>
          )}
          {marketLocale && planner && (
            <div className='border-1 p-2 rounded mt-4'>
              <p className='mb-2'>Toggle fragments based on showInPages</p>
              {switchConfigs.some((x) => x.isVisible) ? (
                switchConfigs.map((config, index) => (
                  <PreviewSwitchControl
                    key={config.id}
                    config={config}
                    activeComponent={activeComponent}
                    defaultSelectedComponent={index === 0}
                    onToggle={onToggle}
                    className='mb-4'
                  />
                ))
              ) : (
                <p className='flex items-center justify-between text-red-500'>
                  No showInPages enabled
                  <CircleAlert size={24} color='#fb2c36' />
                </p>
              )}
            </div>
          )}
        </div>
      </div>
      <div className='w-full pb-20 overflow-y-auto max-h-[75vh] pr-8'>
        {toggleJson && (
          <pre className='whitespace-normal'>
            {Object.keys(translations).map((translation) => (
              <p className='leading-6 mb-2' key={translation}>
                <span
                  className={`font-bold mr-1 p-1 ${theme === 'dark' ? 'bg-white text-black' : 'bg-black text-white'}`}
                >
                  {translation}
                </span>
                <span className='font-light'>:{translations[translation]}</span>
              </p>
            ))}
          </pre>
        )}
        {translations &&
          Object.keys(translations).length > 0 &&
          !toggleJson && (
            <>
              {activeComponent === 'largeBanner' && (
                <BannerLargePreview
                  phraseStrings={translations ?? {}}
                  imageUrl={
                    getMarketOverrideValues().imageUrl || planner.imageUrl
                  }
                  headerVisible={false}
                  showInPages={planner.showInPages}
                  className='w-full'
                />
              )}
              {activeComponent === 'miniBanner' && (
                <BannerMiniLinkPreview
                  phraseStrings={translations ?? {}}
                  headerVisible={false}
                  showInPages={planner.showInPages}
                  className='w-full'
                />
              )}
              {activeComponent === 'contentPage' && (
                <PlannerContentPagePreview
                  contentPageVariant={planner.contentPageVariant}
                  imageUrl={
                    getMarketOverrideValues().imageUrl || planner.imageUrl
                  }
                  phraseStrings={translations}
                  plannerUrl={planner.plannerUrl}
                  secondaryBannerImgUrl={
                    getMarketOverrideValues().secondaryBannerImgUrl ||
                    planner.secondaryBannerImgUrl
                  }
                  headerVisible={false}
                  dynamicView={planner.contentPageSectionVisibility}
                  showInPages={planner.showInPages}
                  videoUrl={getMarketOverrideValues().videoUrl}
                />
              )}
            </>
          )}
      </div>
    </div>
  );
}

export default FragmentPreview;
